import './recent-order.css'

import { EventEmitter } from 'eventemitter3';
import { EventTypes } from '../../../core/event-bus';
import { Util } from '../../../utils/util';
import { Data } from '../../../models/data';
import { Modals, Pages } from '../../../types/enums';
import { ProfileService } from "../../../services/profile-service";
import { IUserRecentOrder, IUserRecentOrderItem, IUserRecentOrderDetail, IUserRecentOrderDetails, IUserRecentOrderItemModifier } from "../../../models/user.interface";
import { OrderService } from '../../../services/order-service';
import { IOrderItem, IOrderItemModifier } from '../../../models/order.interface';
import { ICustomizeItemProps } from '../../modals/CustomizeItem/customize-item.interface';
import { DataLayerService } from '../../../services/data-layer-service';
import { TimedMessage } from '../TimedMessage/timed-message';
import { Dialog } from '../../modals/Dialog/dialog';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { OnlineOrderingUtil } from '../../../online-ordering/online-ordering-util';
import { IName } from '@ts/interfaces/global.interfaces';

export class RecentOrders {
    private readonly _eventBus: EventEmitter;
    private readonly _profileService: ProfileService;
    private readonly _orderService: OrderService;
    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _localizationService: LocalizationService;
    private readonly _dataLayer: DataLayerService;

    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, profileService: ProfileService, orderService: OrderService, dataLayer: DataLayerService) {
        this._eventBus = eventBus;
        this._profileService = profileService;
        this._orderService = orderService;
        this._data = data;
        this._context = context;
        this._localizationService = localizationService;
        this._dataLayer = dataLayer;
    }

    /**
     * Build the 'Recently Ordered Items' section
     *  1. Loads the full set of orders (if not in cache).
     *  2. Renders them.
     *  3. Adds "Show Earlier Orders" button if we have more to load.
     *  4. If no orders exist, renders a message.
     *  @param {string} divId - the id of the div for recent orders to be rendered
     */
    public buildRecentlyOrderedItemsSection = async (divId: string): Promise<void> => {
        const initialOrders = await this._initUserRecentOrders();

        const parent = document.getElementById(divId);
        if (!parent) {
            console.error("Parent element not found.");
            return;
        }

        parent.innerHTML = ""; //clear potential previous data
        parent.classList.add("recent-orders-parent");

        if (!initialOrders || initialOrders.recentOrders.length === 0) {
            this._renderNoRecentOrdersMessage(parent);
            return;
        }

        this._renderOrderList(initialOrders, parent);
        const hasMoreOrders = this._profileService.user.getProperty("RecentOrders")?.orderInfo.hasMoreOrders ?? false;
        if (hasMoreOrders) {
            this.addShowEarlierOrdersButton(parent);
        }
    };

    /**
    * Renders the passed-in order list. 
    * If the "Show Earlier Orders" button exists, we insert above it; 
    * otherwise, we append to the parent.
    */
    private readonly _renderOrderList = (userOrders: IUserRecentOrderDetails | { recentOrders: IUserRecentOrder[] }, parent: HTMLElement): void => {
        userOrders.recentOrders.forEach((order) => {
            const orderCard = document.createElement("div");
            orderCard.classList.add("recent-order-main-card");

            const header = this._renderOrderHeader(order);
            const details = this._renderOrderDetails(order);
            const itemsContainer = this._renderOrderItems(order);
            const footer = this._renderOrderFooter(order);

            // Append sections to order card
            orderCard.appendChild(header);
            orderCard.appendChild(details);
            orderCard.appendChild(itemsContainer);
            if (!this._isOrderAvailableAny(order.orderItems, this._orderService.order.getProperty("OrderTypeID")) && this._data.storeKey) {
                itemsContainer.classList.add("transparent");
                orderCard.appendChild(Util.createHtmlElementFromTemplate(`<div class="unavailable">${this._localizationService.names("NoItemsAvailable")}</div>`))
            }
            orderCard.appendChild(footer);

            // Insert into parent
            const existingButton = parent.querySelector(".show-earlier-orders-btn") as HTMLElement;
            if (existingButton) {
                const wrapperCard = existingButton.closest(".recent-order-main-card") as HTMLElement;
                if (wrapperCard && wrapperCard.parentNode === parent) {
                    parent.insertBefore(orderCard, wrapperCard);
                } else {
                    parent.appendChild(orderCard);
                }
            } else {
                parent.appendChild(orderCard);
            }
        })
    };

    /**
     * Renders the header section of a recent order card.
     *
     * @private
     * @param {IUserRecentOrder} order - The recent order data to render.
     * @returns {HTMLDivElement} The DOM element representing the order header.
     */
    private _renderOrderHeader = (order: IUserRecentOrder): HTMLDivElement => {
        const orderTypeName = this._localizationService.GetLocalized(
            order.orderTypeName,
            this._profileService.user.getProperty("CultureCode") || "en-US"
        );

        const header = document.createElement("div");
        header.classList.add("recent-order-card-header");
        header.innerHTML = `
        <span class="order-type">${orderTypeName}</span>
        <span class="order-status">${this._getOrderStatusText(order.orderStatusId)}</span>`;

        return header;
    };

    /**
     * Renders the details section of a recent order card, including address and formatted date.
     *
     * @private
     * @param {IUserRecentOrder} order - The recent order data to render.
     * @returns {HTMLDivElement} The DOM element containing order details.
     */
    private _renderOrderDetails = (order: IUserRecentOrder): HTMLDivElement => {
        const options: Intl.DateTimeFormatOptions = {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
        };

        const formattedDate = order.busDate
            ? Util.formatDateWithOptions(
                order.busDate,
                this._profileService.user.getProperty("CultureCode") || "en-US",
                options
            )
            : "Unknown Date";

        const details = document.createElement("div");
        details.classList.add("recent-order-card-details");
        details.innerHTML = `
        <p class="recent-order-address">${order.address}</p>
        <p class="recent-order-date">${formattedDate}</p>`;

        return details;
    };

    /**
     * Renders the list of items for a recent order, including action buttons and availability states.
     *
     * @private
     * @param {IUserRecentOrder} order - The recent order data containing items to render.
     * @returns {HTMLDivElement} The DOM element containing all rendered order items.
     */
    private _renderOrderItems = (order: IUserRecentOrder): HTMLDivElement => {
        const filteredOrderItems = this._filterHalfnHalfItems(order.orderItems);
        const itemsContainer = document.createElement("div");
        itemsContainer.id = `recent-order-items-${order.orderId}`;
        itemsContainer.classList.add("recent-order-card-items");

        const isOrderAvailable = this._isOrderAvailable(order.orderItems);

        filteredOrderItems.forEach((item, index) => {
            const actionsContainer = document.createElement("div");
            actionsContainer.classList.add("recent-order-card-item");
            const itemOptions = document.createElement("div");
            itemOptions.classList.add("recent-order-item-actions");
            actionsContainer.appendChild(itemOptions);

            const customizeBtn = this._createActionButton("Customize", "", true, order, item, index);
            const addToOrderBtn = this._createActionButton("AddToOrder", "", true, order, item);
            const unavailable = Util.createHtmlElementFromTemplate(
                `<span class="unavailable">${this._localizationService.names("Unavailable")}</span>`
            );
            const unavailableSize = Util.createHtmlElementFromTemplate(
                `<span class="unavailable">${this._localizationService.names("UnavailableSize")}</span>`
            );

            if (
                this._isItemAvailableExact(item, this._orderService.order.getProperty("OrderTypeID")) ||
                !this._data.storeKey
            ) {
                itemOptions.appendChild(customizeBtn);
                itemOptions.appendChild(addToOrderBtn);
            } else if (this._isItemAvailable(item)) {
                itemOptions.appendChild(customizeBtn);
                itemOptions.appendChild(unavailableSize);
            } else if (isOrderAvailable) {
                itemOptions.appendChild(unavailable);
            }

            const itemCard = this._generateItemCard(item, order.orderTypeId);
            itemCard.appendChild(actionsContainer);
            itemsContainer.appendChild(itemCard);
        });

        return itemsContainer;
    };

    /**
     * Renders the footer of a recent order card, showing either a reorder or track order button based on status.
     *
     * @private
     * @param {IUserRecentOrder} order - The recent order data to determine footer actions.
     * @returns {HTMLDivElement} The DOM element representing the order footer.
     */
    private _renderOrderFooter = (order: IUserRecentOrder): HTMLDivElement => {
        const footer = document.createElement("div");
        footer.classList.add("recent-order-footer");

        if (order.orderStatusId === 0) {
            const reorderBtn = this._createActionButton("Reorder", "", false, order);

            if (this._data.storeKey) {
                if (!this._isOrderAvailableAny(
                    order.orderItems,
                    this._orderService.order.getProperty("OrderTypeID")
                )) {
                    reorderBtn.onclick = () => { };
                    reorderBtn.classList.add("transparent");
                }                
            } else {
                reorderBtn.onclick = () => {
                    this._handleStoreUnselected(order);
                };                    
            }
            footer.appendChild(reorderBtn);
        } else if(order.showTracking) {
            const trackOrderBtn = document.createElement("button");
            trackOrderBtn.innerText = this._localizationService.names("TrackOrder");
            trackOrderBtn.setAttribute("ltag", "TrackOrder");
            trackOrderBtn.id = `btn_track_order_${order.orderId}`;
            trackOrderBtn.name = "btn_track_order";
            trackOrderBtn.dataset.id = order.orderId.toString();
            trackOrderBtn.onclick = () => {
              this._eventBus.emit(
                EventTypes.LOCATION_CHANGE,
                Pages.TRACK_ORDER,
                { orderId: order.orderId, fromRecentOrdersPage: true }
              )

              if (this._dataLayer)
                this._dataLayer.track_order_click(order.orderId, this._data.getProperty("StoreInfo").storeCode.toString());

            }
            footer.appendChild(trackOrderBtn);
        }

        return footer;
    };

    /**
     * Generates a card element for a single order item, including image, details, and modifiers.
     *
     * @private
     * @param {IUserRecentOrderItem} item - The individual order item data.
     * @param {number} orderTypeId - The type ID of the order, used for availability logic.
     * @returns {HTMLDivElement} The DOM element representing the item card.
     */
    private _generateItemCard(
        item: IUserRecentOrderItem,
        orderTypeId: number
    ): HTMLDivElement {
        const itemImageElement = this._getItemImageElement(item);
        const itemDetailsElement = this._getItemDetailsElement(item, orderTypeId);
        const itemModifiersElement = this._getModifiersElement(
            item.orderItemModifiers || [],
            item.halfCount > 1
        );

        const itemImageAndDetails = document.createElement("div");
        itemImageAndDetails.classList.add("menu-item-cart-card");

        if (!this._isItemAvailable(item) && this._data.storeKey) {
            itemImageAndDetails.classList.add("transparent");
            itemModifiersElement?.classList.add("transparent");
        }

        itemImageAndDetails.appendChild(itemImageElement);
        itemImageAndDetails.appendChild(itemDetailsElement);

        const itemCard = document.createElement("div");
        itemCard.appendChild(itemImageAndDetails);
        if (itemModifiersElement) itemCard.appendChild(itemModifiersElement);

        return itemCard;
    }

    /**
     * Determines if at least one item in the order is available in the menu.
     * @param items Array of recent order items to check.
     * @returns True if at least one item exists in the current item catalog.
     */
    private readonly _isOrderAvailable = (items: IUserRecentOrderItem[]): boolean => {
        let available = false;
        for (const item of items) {
            available = available || this._isItemAvailable(item);
        }

        return available;
    }

    /**
     * Determines if any item in the order is available with size in the menu.
     * @param items Array of recent order items to check.
     * @param orderTypeId The current order type (e.g., pickup, delivery).
     * @returns True if any item matches both item existence and valid size for the order type.
     */
    private readonly _isOrderAvailableAny = (items: IUserRecentOrderItem[], orderTypeId: number): boolean => {
        let available = false;
        for (const item of items) {
            available = available || this._isItemAvailableExact(item, orderTypeId);
        }

        return available;
    }

    /**
     * Checks if all items in the order are available in the menu with the current size.
     * Use this when you need to ensure every item and its size is still offered.
     * @param items Array of recent order items to check.
     * @param orderTypeId The current order type (e.g., pickup, delivery).
     * @returns True only if every item exists and the size is valid for the order type.
     */
    private readonly _isOrderAvailableExact = (items: IUserRecentOrderItem[], orderTypeId: number): boolean => {
        let available = true;
        for (const item of items) {
            available = available && this._isItemAvailableExact(item, orderTypeId);
        }

        return available;
    }

    /**
     * Checks if a single item exists on the menu.
     * @param item The recent order item to verify.
     * @returns True if the item ID can be found in the menu data.
     */
    private readonly _isItemAvailable = (item: IUserRecentOrderItem): boolean => {
        const mItem = this._data.getProperty("Items").find(x => x.ItemId == item.itemId);
        return !!mItem;
    }

    /**
     * Checks if a single item with a specific size is available for the given order type.
     * @param item The recent order item to verify (including size).
     * @param orderTypeId The current order type (e.g., pickup, delivery).
     * @returns True if the item exists and the requested size is offered.
     */
    private readonly _isItemAvailableExact = (item: IUserRecentOrderItem, orderTypeId: number): boolean => {
        const mItem = this._data.getProperty("Items").find(x => x.ItemId == item.itemId);

        if (mItem) {
            const sizes = OnlineOrderingUtil.GetItemSizes(mItem, orderTypeId);
            if (sizes.find(x => x.SizeId == item.sizeId)) {
                return true;
            }
        }

        return false;
    }

    private _getItemImageElement(item: IUserRecentOrderItem): HTMLDivElement {
        const imgElement = document.createElement("img");
        imgElement.src = item.imageURL || "";
        imgElement.alt = this._getItemName(item);

        const imgContainer = document.createElement("div");
        imgContainer.classList.add("menu-item-cart-card-image");
        imgContainer.appendChild(imgElement);
        return imgContainer;
    }

    private _getItemDetailsElement(item: IUserRecentOrderItem, orderTypeId: number): HTMLDivElement {
        const detailsElement = document.createElement("div");
        detailsElement.classList.add("menu-item-cart-card-details");

        detailsElement.appendChild(this._getItemNameElement(item));
        detailsElement.appendChild(this._getItemSizeElement(item));
        detailsElement.appendChild(this._getItemCaloriesElement(item, orderTypeId));

        return detailsElement;
    }

    private _getItemNameElement(item: IUserRecentOrderItem): HTMLHeadingElement {
        const nameElement = document.createElement("h3");
        nameElement.classList.add("item-title");
        nameElement.innerText = this._getItemName(item);
        return nameElement;
    }

    private _getItemName(item: IUserRecentOrderItem): string {
        const name = this._localizationService.GetLocalized(item.name);
        return name || "";
    }

    private _getItemSizeElement(item: IUserRecentOrderItem): HTMLParagraphElement {
        let sizeElement = document.createElement("p");

        let sizeName = "";
        const size = this._data.getProperty("Sizes").find(x => x.SizeId === item.sizeId) || null;

        if (size) {
            sizeName = this._localizationService.GetName(size.Names);
        }

        sizeElement.innerText = sizeName;
        sizeElement.classList.add("item-description");
        return sizeElement;
    }

    private _getItemCaloriesElement(itemData: IUserRecentOrderItem, orderTypeId: number): HTMLParagraphElement {
        let { modifier1: modifiers1, modifier2: modifiers2 } = this._getModifiersDataForCalories(itemData);
        let menuItem = this._data.getProperty("Items").find(x => x.ItemId === itemData.itemId);

        let calories: IName[];
        let caloriesElement = document.createElement("p");
        caloriesElement.classList.add("item-calories");

        if (menuItem) {
            calories = OnlineOrderingUtil.getItemCaloriesNew(
                this._localizationService,
                menuItem!,
                modifiers1,
                modifiers2,
                orderTypeId,
                itemData.sizeId,
                this._data.getProperty("DefaultPreModifierId"),
                this._data.getProperty("Settings").DSZID,
                this._context.getProperty("languageCode")
            );
            caloriesElement.innerText = this._localizationService.GetName(calories);
        }

        return caloriesElement;
    }

    private _getModifiersDataForCalories(item: IUserRecentOrderItem): { modifier1: IOrderItemModifier[], modifier2: IOrderItemModifier[] } {
        let modifiers1: IOrderItemModifier[] = [];
        let modifiers2: IOrderItemModifier[] = [];

        item.orderItemModifiers?.forEach(itemModifier => {
            const modifierData = this._data.getProperty("Modifiers").find(x => x.ModifierId === itemModifier.modifierId);

            let modifier = {
                ModifierId: itemModifier.modifierId,
                Name: "",
                Index: itemModifier.halfIndex,
                ModifierDisplayGroupIndex: itemModifier.modifierWebCategoryId,
                iIndex: itemModifier.halfIndex,
                iModifierDisplayGroupIndex: itemModifier.modifierWebCategoryId,
                PreModifierId: itemModifier.preModifierId,
                IsDefault: itemModifier.isDefault,
                ModifierDisplayGroupId: itemModifier.modifierDisplayGroupId,
                MergedModifierDisplayGroupId: itemModifier.mergedModifierDisplayGroupId,
                CountForPricing: itemModifier.countForPricing,
                CountForPricingByItself: itemModifier.countForPricingItself,
                Price: 0,
                IsCrust: modifierData?.IsCrust ?? false
            }
            if (itemModifier.halfIndex <= 1)
                modifiers1.push(modifier);
            else
                modifiers2.push(modifier);
        });

        return {
            modifier1: modifiers1,
            modifier2: modifiers2
        };
    }

    private _getModifiersElement(itemModifiers: IUserRecentOrderItemModifier[], isHalfHalf: boolean): HTMLElement | null {
        let modifiersToDisplay = itemModifiers.map((itemModifier) => OnlineOrderingUtil._mapModifierForDisplay(itemModifier));
        let modifiersText = OnlineOrderingUtil.generateModifiersText(isHalfHalf, modifiersToDisplay, this._localizationService, this._data);
        let modifierElement = document.createElement("div");
        modifierElement.innerHTML = modifiersText;
        return modifierElement;
    }

    /**
     * Creates an action button with event handlers.
     */
    private _createActionButton(label: string, className: string, isLink: boolean, order: IUserRecentOrder, item?: IUserRecentOrderItem, index?: number): HTMLButtonElement {
        const button = document.createElement("button");
        button.innerText = this._localizationService.names(label);
        if (isLink)
            button.classList.add("button-link");
        button.setAttribute("ltag", label);

        if (className)
            button.classList.add(className);

        button.dataset.orderId = order.orderId.toString();
        button.dataset.itemKey = item?.itemKey.toString() ?? "";

        button.onclick = async () => {
            await this._handleOrderItemAction(label, order, item, index);

            if (label == "Reorder" && this._dataLayer) {
                this._dataLayer.re_order_click(label, this._data.getProperty("StoreInfo").storeCode.toString());
            }
        };

        return button;
    }

    /**
     * Handles click events for Customize and Add to Order buttons.
     */
    private async _handleOrderItemAction(action: string, order: IUserRecentOrder, rItem?: IUserRecentOrderItem, index?: number): Promise<void> {
        let orderItems: IOrderItem[] = [];
        let itemNameSet: Set<string> = new Set<string>(); //set of item names that have missing modifiers
        if (!rItem) { //is reorder button
            [orderItems, itemNameSet] = this._retrieveOrderItem(order.orderItems, true);
        }
        else {
            // Find all matching half-and-half items by itemKey (if applicable)
            const fullHalfAndHalfItems = order.orderItems.filter((i) => i.itemKey === rItem.itemKey);

            // If no half-and-half pair exists, process the item as a single item
            const itemsToProcess = fullHalfAndHalfItems.length > 1 ? fullHalfAndHalfItems : [rItem];

            [orderItems, itemNameSet] = this._retrieveOrderItem(itemsToProcess, false);
        }

        switch (action) {
            case "Customize":
                this._handleCustomize(orderItems, order, rItem!, index ?? 0);
                break;
            case "AddToOrder":
                await this._handleAddToOrder(orderItems, order, itemNameSet);
                break;
            case "Reorder":
                await this._handleReorder(orderItems, order, itemNameSet);
                break;
            default:
                console.error("Unknown action:", action);
        }
    }

    /**
     * 
     * Retrieves order items from recent order items
     * @param items
     * @return [IOrderItem[], Set<string>] - array of order items and set of item names that have missing modifiers
     */
    private readonly _retrieveOrderItem = (items: IUserRecentOrderItem[], isReorder: boolean): [IOrderItem[], Set<string>] => {
        let orderItems: IOrderItem[] = [];
        let nextKey = this._orderService.order.getNextItemKey(); //on add to order/customize, its always new item
        let nextRecId = this._orderService.order.getItemRecordId();
        let itemNameSet: Set<string> = new Set<string>();
        const dataItems = this._data.getProperty("Items");

        // Group recent-order items by their own itemKey
        const groups = items.reduce<Map<number, IUserRecentOrderItem[]>>(
            (map, recent) => {
                const grp = map.get(recent.itemKey) ?? [];
                grp.push(recent);
                map.set(recent.itemKey, grp);
                return map;
            },
            new Map()
        );

        for (const group of groups.values()) {
            // assign one global key for the whole group
            const globalKey = nextKey;

            const isHalfGroup = group.some((r) => r.halfIndex > 0);

            for (const recent of group) {
                const dItem = dataItems.find((i) => i.ItemId === recent.itemId);
                if (!dItem) continue;

                const [iModifiers, hasAllModifiers] = this._getItemModifiers(recent);
                const isHalfableValid = (!isHalfGroup) || (isHalfGroup && dItem.IsHalfable);

                if (!hasAllModifiers || !isHalfableValid) {
                    const name = this._localizationService.GetName(dItem.Names);
                    itemNameSet.add(name);
                    if (isReorder) continue; //dont add item to order if missing any modifier for reorder
                }

                const orderItem = this._orderService.order._createOrderItem(dItem, recent.sizeId, recent.halfIndex, isReorder ? recent.quantity : 1, iModifiers);

                orderItem.ItemKey = globalKey;
                orderItem.ItemRecId = nextRecId++;
                orderItem.Edit = true;

                // if it's a half-half group, mark HalfCount
                if (isHalfGroup) {
                    orderItem.HalfCount = 2;
                }

                orderItems.push(orderItem);
            }
            nextKey++;
        }

        return [orderItems, itemNameSet];
    }

    private _getItemModifiers(rItem: IUserRecentOrderItem): [IOrderItemModifier[], boolean] {
        const orderItemModifiers: IOrderItemModifier[] = [];
        let hasAllModifiers: boolean = true;
        if (!rItem.orderItemModifiers?.length) return [orderItemModifiers, hasAllModifiers];

        for (const itemModifier of rItem.orderItemModifiers) {
            const modifier = this._data.getProperty("Modifiers").find(m => m.ModifierId === itemModifier.modifierId);
            if (!modifier) {
                hasAllModifiers = false;
                continue;
            }

            if (rItem.halfIndex !== itemModifier.halfIndex) continue;

            const modifierData = this._data.getProperty("Modifiers").find(x => x.ModifierId === itemModifier.modifierId);

            const oItemModifier: IOrderItemModifier = {
                CountForPricing: itemModifier.countForPricing,
                Index: itemModifier.halfIndex,
                IsDefault: itemModifier.isDefault,
                ModifierDisplayGroupId: itemModifier.modifierDisplayGroupId,
                ModifierId: itemModifier.modifierId,
                Name: JSON.stringify(modifier.Names),
                PreModifierId: itemModifier.preModifierId,
                CountForPricingByItself: itemModifier.countForPricingItself,
                Price: 0,
                iModifierDisplayGroupIndex: itemModifier.modifierWebCategoryId,
                MergedModifierDisplayGroupId: itemModifier.mergedModifierDisplayGroupId,
                iIndex: itemModifier.halfIndex,
                ModifierDisplayGroupIndex: itemModifier.modifierWebCategoryId,
                IsCrust: modifierData?.IsCrust ?? false
            };
            orderItemModifiers.push(oItemModifier);
        }
        return [orderItemModifiers, hasAllModifiers];
    }

    /**
     * Handles customizing an order item.
     */
    private _handleCustomize(items: IOrderItem[], rOrder: IUserRecentOrder, rItem: IUserRecentOrderItem, index: number): void {
        if (!this._data.storeKey) {
            this._handleStoreUnselected(rOrder, rItem);
            return;
        }

        if (items.length === 0) return;

        const orderTypeId = this._orderService.order.getProperty("OrderTypeID");

        if (!orderTypeId) return;

        const dItem = this._data.getProperty("Items").find(i => i.ItemId === items[0].ItemId);

        const isHalfableValid = (rItem.halfCount == 1) || (rItem.halfCount > 1 && dItem?.IsHalfable);

        if (!dItem || !isHalfableValid) {
            new Dialog(
                this._localizationService.names("Error_CustomizeUnable"),
                [
                    { label: this._localizationService.names("Ok").toUpperCase(), value: "no" }
                ]);
            return;
        }

        if (this._dataLayer) {
            this._dataLayer.select_item(this._orderService.order, items[0], index, Pages.ORDERS.replace('/', ''));
        }

        const props: ICustomizeItemProps = {
            sizeId: items[0].SizeId,
            quantity: 1,
            mealDeal: null,
            recentOrder: true,
            mItem: dItem,
            itemsToEdit: items
        };

        this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.CUSTOMIZE_ITEM, props);
    }

    /**
     * Handles adding an order item to the cart.
     */
    private async _handleAddToOrder(items: IOrderItem[], rOrder: IUserRecentOrder, itemNamesSet: Set<string>): Promise<void> {
        if (!this._data.storeKey) {
            this._handleStoreUnselected(rOrder);
            return;
        }

        if (items.length === 0) return;

        if (itemNamesSet?.size > 0) {
            const itemNames = Array.from(itemNamesSet).sort().join(", ");
            new Dialog(
                this._localizationService.names("RecentOrder_NotAllModifiersAvailable").replace("{{itemNames}}", itemNames),
                [
                    { label: this._localizationService.names("Ok").toUpperCase(), value: "no" }
                ]);
            return;
        }

        await this._orderService.order.addItems(this._profileService.loyaltyProvider, items);
        const msg = new TimedMessage(this._localizationService.names("ItemsAddedToCart_Single"), this._localizationService.names("ProceedToCartToCheckout"), 1000);
        msg.show();
    }

    /**
     * Handles reorder
     */
    private async _handleReorder(items: IOrderItem[], rOrder: IUserRecentOrder, itemNamesSet: Set<string>): Promise<void> {
        if (!this._data.storeKey) {
            this._handleStoreUnselected(rOrder);
            return;
        }

        items = items.filter(x => this._isItemAvailableExact({ itemId: x.ItemId, sizeId: x.SizeId } as IUserRecentOrderItem, this._orderService.order.getProperty("OrderTypeID")));

        if (itemNamesSet?.size > 0) {
            const itemNames = Array.from(itemNamesSet).sort().join(", ");
            new Dialog(
                this._localizationService.names("RecentOrder_NotAllModifiersAvailable").replace("{{itemNames}}", itemNames),
                [
                    { label: this._localizationService.names("Ok").toUpperCase(), value: "no" }
                ]);
        }

        if (items.length === 0) return;

        let totalQuantity = 0;
        const groups = items.reduce<Map<number, typeof items>>((map, item) => {
            const key = item.ItemKey;

            if (!map.has(key)) {
                totalQuantity += item.Quantity;
                map.set(key, []);
            }

            map.get(key)!.push(item);
            return map;
        }, new Map());

        for (const groupItems of groups.values()) {
            await this._orderService.order.addItems(this._profileService.loyaltyProvider, groupItems);
        }

        this._runAfterAnyDialogCloses(() => {
            let msg: TimedMessage | null = null;

            if (totalQuantity > 1) {
                msg = new TimedMessage(
                    this._localizationService
                        .names("ItemsAddedToCart_Plural")
                        .replace("{{number}}", totalQuantity.toString()),
                    this._localizationService.names("ProceedToCartToCheckout"),
                    1000
                );
            } else {
                msg = new TimedMessage(
                    this._localizationService.names("ItemsAddedToCart_Single"),
                    this._localizationService.names("ProceedToCartToCheckout"),
                    1000
                );
            }

            msg?.show();
        });
    }

    private _runAfterAnyDialogCloses(fn: () => void): void {
        const dlg = document.querySelector<HTMLDialogElement>("dialog.dialog[open]");
        if (!dlg) {
            fn();
            return;
        }

        let ran = false;
        const runOnce = () => {
            if (ran) return;
            ran = true;
            dlg.removeEventListener("close", runOnce);
            observer.disconnect();
            fn();
        };

        dlg.addEventListener("close", runOnce, { once: true });

        const observer = new MutationObserver(() => {
            if (!document.body.contains(dlg)) runOnce();
        });
        observer.observe(document.body, { childList: true, subtree: true });
    }

    private _handleStoreUnselected(rOrder: IUserRecentOrder, rItem?: IUserRecentOrderItem): void {
        if (!rItem) {
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS, {
                onContinue: () => this._routeToRecentOrder(rOrder),
                onExit: () => this._routeToRecentOrder(rOrder)
            });
        } else { //else if items are passed, route to customize and open modal
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS, {
                onContinue: () => this._routeToRecentOrderCustomize(rOrder, rItem),
                onExit: () => this._routeToRecentOrder(rOrder)
            });
        }
    }

    private _routeToRecentOrder(rOrder: IUserRecentOrder): void {
        this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.ORDERS,
            { divId: `recent-order-items-${rOrder.orderId}` });
    }

    private _routeToRecentOrderCustomize(rOrder: IUserRecentOrder, rItem: IUserRecentOrderItem) {
        this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.ORDERS,
            { divId: `recent-order-items-${rOrder.orderId}`, action: async () => { await this._handleOrderItemAction("Customize", rOrder, rItem); } });
    }

    private readonly _filterHalfnHalfItems = (orderItems: IUserRecentOrderItem[]): IUserRecentOrderItem[] => {
        let halfnHalfItems: IUserRecentOrderItem[] = [];
        let nonHalfnHalfItems: IUserRecentOrderItem[] = [];

        const uniqueItemKeys: number[] = orderItems.reduce((acc: number[], current: IUserRecentOrderItem) => {
            const x = acc.find(item => item === current.itemKey);
            if (!x) {
                return acc.concat([current.itemKey]);
            } else {
                return acc;
            }
        }, []);

        //ignore half n half items that have two different ItemIDs
        const halfnHalfItemKeys = uniqueItemKeys.filter(item => {
            const itemsWithSameKey = orderItems.filter(orderItem => orderItem.itemKey === item);
            return itemsWithSameKey.length > 1 && !this._hasDifferentItemIDs(itemsWithSameKey);
        });

        const nonHalfnHalfItemKeys = uniqueItemKeys.filter(item => orderItems.filter(orderItem => orderItem.itemKey === item).length === 1);

        halfnHalfItems = orderItems.filter(orderItem => halfnHalfItemKeys.filter(item => item === orderItem.itemKey && orderItem.halfIndex === 1).length > 0);
        nonHalfnHalfItems = orderItems.filter(orderItem => nonHalfnHalfItemKeys.filter(item => item === orderItem.itemKey).length > 0);
        return halfnHalfItems.concat(nonHalfnHalfItems);
    }

    private readonly _hasDifferentItemIDs = (orderItems: IUserRecentOrderItem[]): boolean => {
        const itemIds = new Set(orderItems.map(item => item.itemId));
        return itemIds.size > 1;
    }

    /**
     * Creates a "Show Earlier Orders" button if one doesn't already exist.
     * On click, fetches and renders only the NEW items.
     * If none remain, removes itself.
     * @param {HTMLElement} parent - the parent element to which the button will be appended
     */
    public addShowEarlierOrdersButton = (parent: HTMLElement): void => {
        const existingButton = parent.querySelector(".show-earlier-orders-btn") as HTMLButtonElement;
        if (existingButton) {
            return;
        }

        const button = document.createElement("button");
        button.classList.add("secondary", "show-earlier-orders-btn");
        button.innerText = this._localizationService.names("ShowEarlierOrders");

        button.addEventListener("click", async () => {
            const newOrders = await this._getMoreUserRecentOrders();
            if (!newOrders || newOrders.length === 0) return;

            const firstNewOrderIndex = parent.children.length - 2;
            this._renderOrderList({ recentOrders: newOrders }, parent);

            if (firstNewOrderIndex < 0) return;

            const firstNewOrder = parent.children.item(firstNewOrderIndex) as HTMLElement;
            if (firstNewOrder) {
                const cardFooter = firstNewOrder.querySelector<HTMLElement>(".recent-order-footer");
                if (cardFooter) {
                    cardFooter.scrollIntoView({ behavior: "smooth", block: "start", inline: "center" });
                }
            }

            const hasMoreOrders = this._profileService.user.getProperty("RecentOrders")?.orderInfo.hasMoreOrders ?? false;
            if (!hasMoreOrders) {
                button.remove();
            }
        });

        //Hack to make buttons the same width without using a listener
        const cardWrapper = document.createElement("div");
        cardWrapper.classList.add("recent-order-main-card", "pseudo");

        const footer = document.createElement("div");
        footer.classList.add("recent-order-footer", "pseudo");

        footer.appendChild(button);
        cardWrapper.appendChild(footer);
        parent.appendChild(cardWrapper);
    };

    private readonly _renderNoRecentOrdersMessage = (parent: HTMLElement): void => {
        const html = `
            <div class="no-recent-orders-message">
                <p>${this._localizationService.names("NoRecentOrders")}</p>
            </div>`;

        const element = Util.createHtmlElementFromTemplate(html);
        parent.appendChild(element);
    };

    private readonly _initUserRecentOrders = async (): Promise<IUserRecentOrderDetails | null> => {
        const result = await this._fetchRecentOrders(true);
        if (!result || !result.allOrders) {
            return null;
        }
        return result.allOrders;
    };

    private readonly _getMoreUserRecentOrders = async (): Promise<IUserRecentOrder[] | null> => {
        const result = await this._fetchRecentOrders(false);
        if (!result || result.newOrders.length === 0) {
            return null;
        }
        return result.newOrders;
    };

    /**
     * Fetches recent orders by page.
     * 
     * Returns:
     *   - allOrders: The merged set of all known orders so far
     *   - newOrders: Only the newly fetched orders from the last fetch
     *   - hasMore:   Whether more orders remain to be fetched
     *   @param {boolean} isInit - whether this is the first fetch
     */
    private readonly _fetchRecentOrders = async (isInit: boolean): Promise<{ allOrders: IUserRecentOrderDetails | null; newOrders: IUserRecentOrder[]; hasMore: boolean; } | null> => {
        if (!this._profileService.user.isLoggedIn())
            return null;

        let userOrders = this._profileService.user.getProperty("RecentOrders") as IUserRecentOrderDetail | null;

        const oldCount = userOrders?.orderDetails?.recentOrders.length ?? 0;

        if (isInit && userOrders) {
            return {
                allOrders: userOrders.orderDetails,
                newOrders: [],
                hasMore: userOrders.orderInfo.hasMoreOrders,
            };
        }

        if (!userOrders) { //init user orders if doesnt exist
            userOrders = {
                orderInfo: {
                    pageNumber: 0,
                    hasMoreOrders: false
                },
                orderDetails: {
                    pagination: 0,
                    recentOrders: []
                }
            };
        }

        const data = await this._orderService.getRecentOrdersByPage(this._profileService.user.getProperty("RecentOrders")?.orderInfo.pageNumber || 0);

        // 1) If data.pagination=1 => more orders remain
        if (data.pagination === 1) {
            userOrders.orderInfo.hasMoreOrders = true;
            userOrders.orderInfo.pageNumber += 1;
        } else {
            userOrders.orderInfo.hasMoreOrders = false;
        }

        if (data.recentOrders && data.recentOrders.length > 0) {
            if (!userOrders.orderDetails) {
                // No existing orderDetails => just set them
                userOrders.orderDetails = {
                    pagination: data.pagination,
                    recentOrders: data.recentOrders
                };
            } else {
                // Already have orderDetails => merge
                userOrders.orderDetails.pagination = data.pagination;
                userOrders.orderDetails.recentOrders = [
                    ...userOrders.orderDetails.recentOrders,
                    ...data.recentOrders
                ];
            }
        }

        const newCount = userOrders.orderDetails.recentOrders.length ?? 0;
        const newlyFetched = userOrders
            ? userOrders.orderDetails.recentOrders.slice(oldCount, newCount)
            : [];

        const hasMore = data.pagination === 1;
        this._profileService.user.updateUser({ RecentOrders: userOrders });

        return {
            allOrders: userOrders.orderDetails,
            newOrders: newlyFetched,
            hasMore
        };
    };

    /**
     * Utility method to get order status text
     */
    private _getOrderStatusText(statusId: number): string {
        switch (statusId) {
            case 0:
                return this._localizationService.names("Complete");
            case 1:
                return this._localizationService.names("Future");
            case 2:
                return this._localizationService.names("InProgress");
            default:
                return "";
        }
    }
}